/**
 * Page-wide bookkeeping for overlays that close on the browser Back button.
 *
 * Each open overlay reserves one history entry, pushed without a URL change. Which overlay a Back
 * press closes is tracked here in memory, deliberately NOT in `window.history.state`: the Next.js
 * App Router rewrites the history state on router commits and drops foreign keys, which is how a
 * sub-modal's close used to also close the modal under it.
 *
 * The model is a count, not an identity. Every entry this module pushes sits at the same URL, so
 * which one the browser popped is both unknowable and irrelevant — all that matters is how many
 * were pushed and how many still have a live overlay behind them. A pop closes the top live
 * overlay; a pop that lands on a reservation (an entry whose overlay went away without a Back
 * press) is passed on with another `goBack`, so the press reaches the navigation the user actually
 * asked for.
 *
 * Nothing here waits for a `popstate` it triggered itself, and closing an overlay never touches
 * history — so there is no in-flight state to reconcile and no ordering to get wrong.
 *
 * The one thing that does go into history state is an ownership flag on the entries this module
 * pushes. An entry a Back press has already passed through, or closed an overlay from, is still in
 * the forward history: Forward walks onto it, and Back from a later page lands on it. Without the
 * flag such an entry is indistinguishable from the page's own, and the next Back press from it is a
 * same-URL no-op. With it, the entry is adopted back as a reservation and that press passes through.
 * The flag is a plain yes: a router that rewrites the state can only remove it, never add it, so a
 * lost flag degrades to the no-op and can never close or skip anything that is not ours.
 *
 * A reservation is only reclaimed or passed on while the URL still matches the one it was pushed
 * at. Entries stranded by a navigation are left alone: a later Back press spends itself on a
 * same-URL no-op rather than risking a URL change the user didn't ask for.
 *
 * Reclaiming additionally requires the history depth to be unchanged, because a matching URL alone
 * does not mean the reserved entry is still the one we are standing on: leaving the page and coming
 * back by two pushes returns to the same URL with the reservation now buried, and taking it over
 * would leave the overlay with no entry at all — the Back press would then leave the page instead
 * of closing it. A depth that moved falls back to pushing a fresh entry, which is always safe.
 */

export interface OverlayBackEntry {
    overlayId: string
    /** URL when the overlay opened. The manager only acts on entries matching the current URL. */
    href: string
    /** History depth of the reserved entry, used to tell it apart from a later entry at the same URL. */
    depth: number
    onClose: () => void
}

/** The history operations the manager needs, so it can be driven without a real browser. */
export interface OverlayHistory {
    getHref: () => string
    /**
     * How deep the session history is. Only ever compared for equality, so any counter that moves
     * on a push and holds still on a back/forward will do.
     */
    getDepth: () => number
    /** Adds one history entry at the current URL, flagged as this module's own. */
    pushEntry: () => void
    /** Goes back one entry. Fires a `popstate` in a real browser, which is why it is never awaited. */
    goBack: () => void
    /**
     * Subscribes to history traversal. The listener learns whether the entry landed on carries this
     * module's ownership flag.
     */
    onPopState: (listener: (landedOnOwnEntry: boolean) => void) => void
}

interface OverlayBackReservation {
    href: string
    depth: number
}

export const createOverlayBackManager = (history: OverlayHistory) => {
    const liveOverlays: OverlayBackEntry[] = []
    /** Pushed entries no overlay owns any more, most recent last. */
    const reservations: OverlayBackReservation[] = []
    let isListening = false

    const onPopState = (landedOnOwnEntry: boolean) => {
        const href = history.getHref()
        const top = liveOverlays[liveOverlays.length - 1]

        if (top && top.href === href) {
            // The press spent one of our entries and an overlay is open: close it, top first.
            liveOverlays.pop()
            top.onClose()
            return
        }

        // Depth is deliberately not checked here: a press arriving after a navigation still landed
        // on a phantom entry at this URL, and passing it on is what keeps that press from being
        // spent on a no-op.
        if (reservations[reservations.length - 1]?.href === href) {
            // It landed on an entry nothing owns any more, so it closed nothing. Pass the press on
            // rather than swallowing it.
            reservations.pop()
            history.goBack()
            return
        }

        if (landedOnOwnEntry) {
            // We are standing on an entry we pushed and had lost track of. Take it back as a
            // reservation, so the next Back press passes through it instead of doing nothing.
            reservations.push({ href, depth: history.getDepth() })
            return
        }

        // A real navigation — not ours to interpret.
    }

    /**
     * Reserves a history entry for an opened overlay and returns its handle.
     *
     * Re-registering an overlay that is still open keeps its entry, and an overlay opening on the
     * entry a released overlay left behind takes that entry over instead of stacking another one.
     * Between them, a setup → cleanup → setup cycle (React StrictMode remounting) ends up with
     * exactly one entry, with no need to tell a remount apart from a real unmount.
     */
    const registerOverlay = (overlayId: string, onClose: () => void): OverlayBackEntry => {
        if (!isListening) {
            isListening = true
            history.onPopState(onPopState)
        }

        const href = history.getHref()
        const live = liveOverlays.find((entry) => entry.overlayId === overlayId)

        if (live) {
            // Two overlays open at once under one id would share a single entry, so only the last
            // one registered could ever be closed by a Back press. Ids are expected to be unique
            // per open overlay; a remount releases its entry first and so never lands here.
            if (process.env.NODE_ENV !== 'production') {
                console.warn(
                    `useCloseOnBack: overlay "${overlayId}" is already open. Give each overlay its own overlayId, or only one of them will close on Back.`,
                )
            }

            live.onClose = onClose
            return live
        }

        const depth = history.getDepth()
        const reservation = reservations.findIndex(
            (candidate) => candidate.href === href && candidate.depth === depth,
        )

        if (reservation === -1) {
            history.pushEntry()
        } else {
            reservations.splice(reservation, 1)
        }

        const entry: OverlayBackEntry = { overlayId, href, depth: history.getDepth(), onClose }
        liveOverlays.push(entry)

        return entry
    }

    /**
     * Gives up an overlay's claim on its history entry, for every way an overlay can stop being
     * open: the close button, Escape, a backdrop click, a bypassing `hide()`, or the component
     * unmounting. The entry becomes a reservation rather than being consumed, so no `popstate` is
     * set in motion. A no-op once a Back press has already consumed the entry.
     */
    const releaseOverlay = (entry: OverlayBackEntry): void => {
        const index = liveOverlays.indexOf(entry)

        if (index === -1) {
            return
        }

        liveOverlays.splice(index, 1)
        reservations.push({ href: entry.href, depth: entry.depth })
    }

    return { registerOverlay, releaseOverlay }
}

/**
 * Marks an entry as pushed by this module. Sits next to the router's own keys in the entry's
 * state. The App Router keeps state it recognises by its `__NA` flag and drops every other key on
 * its next commit, so the flag is read as a hint and its absence proves nothing.
 */
const OWN_ENTRY_KEY = '__trustooOverlayEntry'

const browserHistory: OverlayHistory = {
    getHref: () => window.location.href,
    // Grows on every push and holds still on a back or a forward, which is all the equality check
    // needs. Safari caps it at 50, and a cap only ever makes the check fail — costing one extra
    // history entry, never a missed close.
    getDepth: () => window.history.length,
    // Pass the current state through so router-owned keys (Next internals, react-router's index)
    // survive onto the new entry, and omit the URL argument — the App Router's patched pushState
    // treats any URL as an external navigation and dispatches a router update.
    pushEntry: () =>
        window.history.pushState({ ...window.history.state, [OWN_ENTRY_KEY]: true }, ''),
    goBack: () => window.history.back(),
    onPopState: (listener) =>
        window.addEventListener('popstate', (event) =>
            listener(event.state?.[OWN_ENTRY_KEY] === true),
        ),
}

/**
 * Built on first use rather than at module scope: this module is reachable from code that also runs
 * on the server, where `window` does not exist. One manager per page — a module re-evaluation (dev
 * HMR) starts fresh while the real history entries remain; their ownership flags let the fresh
 * manager adopt them back as it lands on them.
 */
let browserManager: ReturnType<typeof createOverlayBackManager> | null = null

const getBrowserManager = () => {
    if (!browserManager) {
        browserManager = createOverlayBackManager(browserHistory)
    }

    return browserManager
}

export const registerOverlay = (overlayId: string, onClose: () => void): OverlayBackEntry =>
    getBrowserManager().registerOverlay(overlayId, onClose)

export const releaseOverlay = (entry: OverlayBackEntry): void =>
    getBrowserManager().releaseOverlay(entry)
